import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import SmartImage from "@/components/SmartImage";
import { getRecentlyViewed } from "@/lib/recentlyViewed";

/**
 * Horizontal rail of the last N products the user viewed.
 *
 * Renders TWO variants:
 *   - Mobile (< md): compact 5-tile strip identical to the mobile drawer
 *     (68×80 tiles, 2-line label) so shoppers rediscover pieces from the
 *     home + PDP without leaving the fold.
 *   - Desktop (md+): the full ProductCard rail with real price + rating.
 *
 * `excludeSlug` filters the current PDP product. Auto-hides when empty.
 */
const RecentlyViewed = ({ title = "Recently viewed", excludeSlug }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const all = getRecentlyViewed();
    setItems(
      excludeSlug ? all.filter((p) => p.slug !== excludeSlug) : all,
    );
  }, [excludeSlug]);

  if (items.length === 0) return null;

  return (
    <section
      className="lux-container mt-14 md:mt-28"
      data-testid="recently-viewed"
    >
      <div className="flex items-end justify-between mb-4 md:mb-8">
        <h2 className="font-serif-display text-xl md:text-3xl">{title}</h2>
        <span className="text-[10px] md:text-xs uppercase tracking-[0.18em] text-neutral-500">
          {items.length} {items.length === 1 ? "piece" : "pieces"}
        </span>
      </div>

      {/* Mobile compact strip (5 tiles, drawer-style) */}
      <div
        className="md:hidden flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1"
        data-testid="recently-viewed-strip"
      >
        {items.slice(0, 5).map((p) => (
          <Link
            key={`rv-mobile-${p.slug}`}
            to={`/product/${p.slug}`}
            className="flex-shrink-0 w-20"
            data-testid={`recently-viewed-tile-${p.slug}`}
          >
            <div className="w-20 h-24 bg-bg-2 overflow-hidden rounded p-1.5 flex items-center justify-center">
              {p.image ? (
                <SmartImage
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-xl font-extrabold text-pink/30">
                  {(p.name || "?").charAt(0)}
                </span>
              )}
            </div>
            <p className="text-[10px] text-ink-soft mt-1 line-clamp-2 leading-tight">
              {p.name}
            </p>
          </Link>
        ))}
      </div>

      {/* Desktop full ProductCard rail */}
      <div className="hidden md:block no-scrollbar overflow-x-auto -mx-4 px-4">
        <div className="flex gap-4 md:gap-6 min-w-min">
          {items.map((p, i) => (
            <div
              key={p.slug}
              className="w-[45vw] sm:w-[28vw] md:w-[22vw] lg:w-[16vw] flex-shrink-0"
            >
              <ProductCard product={p} index={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecentlyViewed;
